import { isProduction } from './client';

/**
 * QA hook for AIE-23. Off on production deployments.
 *
 * `next dev` stays on even when NEXT_PUBLIC_APP_ENV=prod (cloud agent and
 * `yarn start:production`) so the failure can be forced locally. A real
 * production server has NODE_ENV=production and NEXT_PUBLIC_APP_ENV=prod,
 * and ignores the flag.
 *
 * Set it with `?debugRefresh=stale|expired|empty|fail` or
 * `localStorage['curipod.debugRefresh']`. `?debugRefresh=off` clears it.
 *
 * No-code alternatives that also work on main (no flag there):
 * - Block the /api/auth/session URL in DevTools, then toggle Network offline for
 *   a few seconds. On main every socket error refreshes, so the console fills
 *   with RefreshError "token refresh failed" about every 5s.
 * - Delete the `__Secure-next-auth.session-token` cookie (or
 *   `next-auth.session-token` on http) and do the same.
 *
 * `empty` and `fail` do not redirect to /login. Only a session whose error is
 * RefreshAccessTokenError does — that is the jwt callback's "refresh token is
 * invalid or expired" signal.
 */
export const SESSION_REFRESH_DEBUG_MODES = ['stale', 'expired', 'empty', 'fail'] as const;

export type SessionRefreshDebugMode = typeof SESSION_REFRESH_DEBUG_MODES[number];

export const SESSION_REFRESH_DEBUG_STORAGE_KEY = 'curipod.debugRefresh';

const CLEAR_VALUES = new Set(['off', '0']);

export function sessionRefreshDebugAllowed(): boolean {
  if (process.env.NODE_ENV === 'development') return true;
  return !isProduction();
}

const isMode = (value: string | null): value is SessionRefreshDebugMode =>
  !!value && (SESSION_REFRESH_DEBUG_MODES as readonly string[]).includes(value);

/** Active QA mode, or null. Query param wins and is copied into localStorage. */
export function readSessionRefreshDebugMode(): SessionRefreshDebugMode | null {
  if (!sessionRefreshDebugAllowed() || typeof window === 'undefined') return null;
  try {
    const param = new URLSearchParams(window.location.search).get('debugRefresh');
    if (param && CLEAR_VALUES.has(param)) {
      window.localStorage.removeItem(SESSION_REFRESH_DEBUG_STORAGE_KEY);
      return null;
    }
    if (isMode(param)) {
      window.localStorage.setItem(SESSION_REFRESH_DEBUG_STORAGE_KEY, param);
      return param;
    }
    const stored = window.localStorage.getItem(SESSION_REFRESH_DEBUG_STORAGE_KEY);
    return isMode(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** Unsigned JWT whose `exp` is already in the past. The game server rejects it. */
export function buildExpiredDebugIdToken(now = Date.now()): string {
  const exp = Math.floor(now / 1000) - 120;
  const payload = btoa(JSON.stringify({ exp, sub: 'debug-refresh' }))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `eyJhbGciOiJub25lIn0.${payload}.debug`;
}

/**
 * Token the host socket should present. Any debug mode uses an expired token
 * so the game server answers `Unauthorized` and the recovery path runs.
 */
export function debugSocketToken(realToken: string): string {
  if (!readSessionRefreshDebugMode()) return realToken;
  return buildExpiredDebugIdToken();
}
